import React, { useCallback, useEffect, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Button, CircularProgress, IconButton, Menu, MenuItem, Stack } from "@mui/material";
import EmptyCardSmartSheet from "assets/images/EmptyCardSmartSheet.png";
import { WorkspaceColumnSettingsIcon } from "assets/material-custom-icons";
import { AccessDenied, RoleComponentGuard } from "components";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { InlineFilter } from "components/Filters/InlineFilter";
import { AddNewLine } from "components/Smart-sheet/components/AddNewLine/AddNewLine";
import { MeasureFilters } from "components/Smart-sheet/components/MeasureFilters";
import { SmartSheetDataSettings } from "components/Smart-sheet/components/Smart-sheet-data-settings/SmartSheetDataSettings";
import { SmartSheetSettings } from "components/Smart-sheet/components/Smart-sheet-settings/SmartSheetSettings";
import { SmartSheetTable } from "components/Smart-sheet/components/SmartSheetTable";
import { SmartSheetProvider, useSmartSheet } from "components/Smart-sheet/context/SmartSheet-context";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { useNavigate } from "hooks";
import {
  usePickUserPermissions,
  useQuickViewAddNewLineSelect,
  useQuickViewMetaSelector,
  useQuickViewQueriesSelect,
  useViewActions
} from "store";
import { UserPermissionType } from "store/types";

import { RoleName } from "../../constants";

interface Props {
  containerRef: React.RefObject<HTMLDivElement>;
  isFullscreen: boolean;
  toggleFullscreen: () => void;
}

const SmartSheetContent: React.FC<Props> = ({ containerRef, isFullscreen, toggleFullscreen }) => {
  const {
    refetchTable,
    handleFilterChange,
    handleMeasureFilterChange,
    measureFilters,
    tableData,
    handleClearAll,
    resetSheetSettings,
    hasNonDefaultSettings,
    showEmptyState,
    tableFileLoading,
    exportTable
  } = useSmartSheet();
  const { filters } = useQuickViewQueriesSelect();
  const { dealFilters } = useQuickViewMetaSelector();
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const addNewLineOpen = useQuickViewAddNewLineSelect();
  const { setQuickViewAddNewLineAction } = useViewActions();
  const navigate = useNavigate();
  const smartSheet = usePickUserPermissions("smart_sheet");
  const noAccessPermission = smartSheet && smartSheet === UserPermissionType.NO_ACCESS;

  return (
    <RoleComponentGuard
      availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
      hideChild={noAccessPermission}
      RenderInstead={<AccessDenied redirectClick={() => navigate({ route: "main" })} title="Back home" />}
    >
      <Stack
        sx={{
          gap: "16px",
          overflow: "hidden",
          bgcolor: "#f6f8fc",
          flexGrow: 1,
          alignItems: "stretch"
        }}
      >
        {addNewLineOpen && (
          <AddNewLine onClose={() => setQuickViewAddNewLineAction(false)} refetchTable={refetchTable} />
        )}
        <Stack
          ref={containerRef}
          direction="column"
          gap="16px"
          sx={{
            width: "100%",
            height: "100%",
            flex: 1,
            minHeight: 0,
            borderRadius: "8px",
            background: "#FFF",
            boxShadow: "0 1px 2px 0 rgba(18, 18, 23, 0.10)",
            overflow: "hidden",
            padding: "8px 16px"
          }}
        >
          {showEmptyState ? (
            <EmptyCard
              title={EMPTY_CARD_CONTENT.quickView.title}
              description={{
                text: EMPTY_CARD_CONTENT.quickView.description,
                externalText: EMPTY_CARD_CONTENT.quickView.externalDescription,
                sx: { width: 486 }
              }}
              image={{
                src: EmptyCardSmartSheet,
                sx: { width: 315, height: 140 }
              }}
              fitPage
              actionsElement={
                <Button
                  variant="contained"
                  onClick={() => setQuickViewAddNewLineAction(true)}
                  startIcon={<AddIcon />}
                  sx={{ bgcolor: "#071F35" }}
                >
                  Add Item
                </Button>
              }
            />
          ) : (
            <>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack sx={{ maxHeight: "100px", overflow: "auto" }}>
                  <InlineFilter
                    isQuickView
                    state={filters}
                    filtersList={dealFilters}
                    handleChangeFilter={handleFilterChange}
                    applyMode
                    container={containerRef.current}
                    additionalFilters={
                      tableData &&
                      Object.keys(measureFilters).length > 0 && (
                        <MeasureFilters
                          columns={tableData.columns}
                          measureFilters={measureFilters}
                          onMeasureFilterChange={handleMeasureFilterChange}
                          container={containerRef.current}
                        />
                      )
                    }
                    onClearAdditionalFilters={handleClearAll}
                  />
                </Stack>

                <Stack direction="row" gap={1} alignItems="center">
                  {hasNonDefaultSettings && (
                    <Button size="sm" sx={{ color: "#26A6AE" }} onClick={resetSheetSettings}>
                      Clear All
                    </Button>
                  )}
                  <SmartSheetDataSettings container={containerRef.current} />
                  <Button
                    variant="outlined"
                    startIcon={<WorkspaceColumnSettingsIcon sx={{ fontSize: 24, fill: "#0A3142" }} />}
                    onClick={(e) => setSettingsAnchor(e.currentTarget)}
                    size="sm"
                    sx={{
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      bgcolor: "#E6E9EB",
                      borderColor: "#E6E9EB",
                      "&:hover": {
                        borderColor: "#0A3142",
                        bgcolor: "#E6E9EB"
                      }
                    }}
                  >
                    Set Columns
                  </Button>
                  <IconButton onClick={toggleFullscreen}>
                    {isFullscreen ? (
                      <FullscreenExitIcon sx={{ fontSize: 24, fill: "#0A3142" }} />
                    ) : (
                      <FullscreenIcon sx={{ fontSize: 24, fill: "#0A3142" }} />
                    )}
                  </IconButton>
                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: "flex-end",
                      alignItems: "center",
                      gap: "20px",
                      padding: "0px 15px 0px 0px"
                    }}
                  >
                    <IconButton
                      sx={{ margin: "-8px" }}
                      onClick={(e) => {
                        if (tableFileLoading) return;
                        setMenuAnchor(e.currentTarget);
                      }}
                    >
                      {!tableFileLoading ? <MoreVertIcon sx={{ color: "#0a3142" }} /> : <CircularProgress size={20} />}
                    </IconButton>
                    <Menu
                      anchorEl={menuAnchor}
                      open={!!menuAnchor}
                      onClose={() => setMenuAnchor(null)}
                      container={containerRef.current ?? document.body}
                    >
                      <MenuItem
                        onClick={() => {
                          exportTable(true);
                          setMenuAnchor(null);
                        }}
                      >
                        Export Smart Sheets data grouped by Deal
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          exportTable(false);
                          setMenuAnchor(null);
                        }}
                      >
                        Export Smart Sheets data
                      </MenuItem>
                    </Menu>
                  </Stack>
                </Stack>
              </Stack>

              <SmartSheetTable />

              <SmartSheetSettings
                customMenuOpen={settingsAnchor}
                setCustomMenuOpen={setSettingsAnchor}
                onCloseWithRefetch={refetchTable}
                container={containerRef.current}
              />
            </>
          )}
        </Stack>
      </Stack>
    </RoleComponentGuard>
  );
};

export const SmartSheet: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, [isFullscreen]);

  return (
    <SmartSheetProvider containerRef={containerRef}>
      <SmartSheetContent containerRef={containerRef} isFullscreen={isFullscreen} toggleFullscreen={toggleFullscreen} />
    </SmartSheetProvider>
  );
};
